import React, { useState, useCallback, useRef, KeyboardEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import Chip from '../chip/chip';
import { getInputContainerClasses } from '../input/input';

export enum ChipInputError {
    MAX_LENGTH = 'MAX_LENGTH',
    MAX_CHIPS = 'MAX_CHIPS',
    DUPLICATE = 'DUPLICATE',
    INVALID = 'INVALID',
}

export interface ChipInputProps {
    value: string[];
    name: string;
    id?: string;
    onChange: (values: string[]) => void;
    placeholder?: string;
    onValidate?: (value: string) => boolean | string;
    error?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    className?: string;
    allowDuplicates?: boolean;
    maxChips?: number;
    maxChipLength?: number;
    'data-testid'?: string;
    confirmOnBlur?: boolean;
    onError?: (error: ChipInputError | string | null) => void;
    confirmKeys?: string[];
}

export function ChipInput({
    value,
    name,
    onChange,
    placeholder = 'Add item...',
    onValidate,
    error = false,
    errorMessage,
    disabled = false,
    id,
    className,
    allowDuplicates = false,
    maxChips,
    maxChipLength,
    'data-testid': dataTestId = 'chip-input',
    confirmOnBlur = false,
    onError,
    confirmKeys,
}: ChipInputProps) {
    const [inputValue, setInputValue] = useState('');
    const [validationError, setValidationError] = useState<ChipInputError | string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleValidationError = (error: ChipInputError | string | null) => {
        setValidationError(error);
        if (onError) {
            onError(error);
        }
    };

    const addChip = useCallback(
        (chipValue: string) => {
            const trimmedValue = chipValue.trim();

            if (!trimmedValue) {
                return;
            }

            // Check max chip length
            if (maxChipLength && trimmedValue.length > maxChipLength) {
                handleValidationError(ChipInputError.MAX_LENGTH);
                return;
            }

            // Check max chips limit
            if (maxChips && value.length >= maxChips) {
                handleValidationError(ChipInputError.MAX_CHIPS);
                return;
            }

            // Check for duplicates
            if (!allowDuplicates && value.includes(trimmedValue)) {
                handleValidationError(ChipInputError.DUPLICATE);
                return;
            }

            // Validate if validator is provided
            if (onValidate) {
                const validationResult = onValidate(trimmedValue);
                if (validationResult === false) {
                    handleValidationError(ChipInputError.INVALID);
                    return;
                }
                if (typeof validationResult === 'string') {
                    handleValidationError(validationResult);
                    return;
                }
            }

            // Clear validation error and add chip
            handleValidationError(null);
            onChange([...value, trimmedValue]);
            setInputValue('');
        },
        [value, onChange, onValidate, allowDuplicates, maxChips, maxChipLength, onError],
    );

    const removeChip = useCallback(
        (chipToRemove: string) => {
            onChange(value.filter((chip) => chip !== chipToRemove));
            handleValidationError(null);
        },
        [value, onChange, onError],
    );

    const handleKeyUp = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            const { key } = event;

            // Add chip on Enter, Space, or Comma
            const keysToConfirm = confirmKeys || ['Enter', ' ', ','];
            if (keysToConfirm.includes(key)) {
                event.preventDefault();
                addChip(inputValue);
            }

            // Remove last chip on Backspace when input is empty
            if (key === 'Backspace' && !inputValue && value.length > 0) {
                const lastChip = value[value.length - 1];
                removeChip(lastChip);
            }

            // Also handle Delete key
            if (key === 'Delete' && !inputValue && value.length > 0) {
                const lastChip = value[value.length - 1];
                removeChip(lastChip);
            }
        },
        [inputValue, value, addChip, removeChip, confirmKeys],
    );

    const handleBlur = useCallback(() => {
        console.log('onblur');
        if (confirmOnBlur) {
            addChip(inputValue);
        }
    }, [inputValue, addChip, confirmOnBlur]);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(event.target.value);
            handleValidationError(null);
        },
        [onError],
    );

    const inputContainerClasses = getInputContainerClasses({
        disabled,
        error: !!(error || validationError),
    });

    const containerClasses = twMerge(inputContainerClasses, 'flex flex-wrap py-[10px]', className);

    return (
        <div
            onClick={() => {
                inputRef.current && inputRef.current.focus();
            }}
            className={`w-full cursor-text  ${disabled ? 'cursor-not-allowed' : ''}`}
            data-testid={dataTestId}
        >
            <div className={containerClasses}>
                {value.length > 0 && (
                    <div className="flex flex-wrap w-full pb-2">
                        {value.map((item, index) => (
                            <Chip
                                key={`${item}-${index}`}
                                size="xxs"
                                text={item}
                                onClose={() => removeChip(item)}
                                disabled={disabled}
                                data-testid={`chip-${index}`}
                            />
                        ))}
                    </div>
                )}
                <input
                    name={name}
                    ref={inputRef}
                    id={id}
                    data-testid={`${dataTestId}-input`}
                    placeholder={placeholder}
                    className="w-full h-full border-0 focus:outline-none focus:border-none focus:ring-0 placeholder-shown:text-ellipsis p-0"
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyUp={handleKeyUp}
                    onBlur={handleBlur}
                    disabled={disabled}
                />
            </div>
        </div>
    );
}

export default ChipInput;
